import { Box, Grid, styled, Tooltip } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormSwitch, LwFormInput } from 'redesign';
import { IconQuestion } from 'assets/img';
import type { SettingsEditProps } from './settings-edit.types';
import { CompanySettingsDataForm } from './settings-edit-page.types';

export const SettingsEdit = (props: SettingsEditProps) => {
  const { control } = useFormContext<CompanySettingsDataForm>();
  const { offersEmployeeWork } = props.companyData;
  const { t } = useTranslation('ogp-screen-specific');

  const employeeSwitches = (
    <>
      <FormSwitch
        name="requiresEmployeeContract"
        control={control}
        disabled={!offersEmployeeWork}
        label={t('settings-edit-page.employee-contract-requested-label')}
      />
      <FormSwitch
        name="requiresEmployeeCertificates"
        control={control}
        disabled={!offersEmployeeWork}
        label={t('settings-edit-page.employee-certificates-requested-label')}
      />
    </>
  );

  const employeeMutableUntilHoursField = (
    <Grid item xs={4}>
      <FormFieldContainer>
        <LwFormInput
          name="employeeMutableUntilHours"
          inputType="number"
          control={control}
          label={t('settings-edit-page.employee-mutable-until-minutes-label')}
          placeholder="0"
          rules={{
            required: 'Voer een fee in',
            min: {
              value: 0,
              message: 'Min is 0',
            },
          }}
        />
      </FormFieldContainer>
    </Grid>
  );

  return (
    <Grid container spacing={4}>
      <Grid item xs={4}>
        <SwitchesContainer>
          {offersEmployeeWork ? employeeSwitches : null}
          <TooltipContainer>
            <FormSwitch
              name="allowShiftStartTimeOverride"
              control={control}
              label={t('settings-edit-page.allow-shift-start-time-override-label')}
            />
            <Tooltip
              placement="bottom"
              title={t('settings-edit-page.allow-shift-start-time-override-tooltip-description', {
                ns: 'ogp-screen-specific',
              })}
            >
              <IconWrapper>
                <IconQuestion data-testid="allow-shift-start-time-override-tooltip-icon" />
              </IconWrapper>
            </Tooltip>
          </TooltipContainer>
          <TooltipContainer>
            <FormSwitch
              name="allowShiftEndTimeOverride"
              control={control}
              label={t('settings-edit-page.allow-shift-end-time-override-label')}
            />
            <Tooltip
              placement="bottom"
              title={t('settings-edit-page.allow-shift-end-time-override-tooltip-description', {
                ns: 'ogp-screen-specific',
              })}
            >
              <IconWrapper>
                <IconQuestion data-testid="allow-shift-end-time-override-tooltip-icon" />
              </IconWrapper>
            </Tooltip>
          </TooltipContainer>
        </SwitchesContainer>
      </Grid>
      {offersEmployeeWork ? employeeMutableUntilHoursField : null}
      <Grid item xs={4} />
    </Grid>
  );
};

const FormFieldContainer = styled(Box)(() => ({
  maxWidth: '400px',
  width: '100%',
}));

const SwitchesContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  maxWidth: '400px',
  width: '100%',
}));

const TooltipContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(3),
}));

const IconWrapper = styled('span')(() => ({
  display: 'inline-flex',
  cursor: 'pointer',
  width: '20px',
  height: '20px',
}));
